import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Globe, FileText, ShieldCheck, Bell, GraduationCap, Lightbulb } from "lucide-react";

/* Altara · Understand · v4
 * Beat-timed to Charlotte's narration for the Understand slide:
 *   0.0 – 10.0  PANEL A · Nomsa types a question to NAVI (English) — hits "asks NAVI"
 *  10.0 – 18.0  PANEL B · Language toggle (English → siSwati) — hits "native language"
 *  18.0 – 27.0  PANEL C · Altara features: Grounded · Cited · Signals — hits Altara feature list
 *  27.0 – 35.0  PANEL D · Education becomes contextual. — closing line
 */

const GOLD = "#C9A24A";
const NAVY = "#0A1F44";
const CREAM = "#F5EFE6";
const ACCENT = "#E8DFD0";

const QUESTION_EN = "How do I know if this institution uses AI to make decisions about my investments?";
const QUESTION_SS = "Ngingakwati kanjani kutsi lesi sikhungo sisebentisa i-AI kwenta timfihlo ngetimboti tami?";

const ANSWER_EN_LINES = [
  "Based on the disclosure documents from ABC Wealth,",
  "yes, they use AI for portfolio optimisation.",
  "The specific model is disclosed in Section 4.2.",
];

const ANSWER_SS_LINES = [
  "Ngekusekelwa etincwadzini letivetiwe te-ABC Wealth,",
  "yebo, basebentisa i-AI kwenta i-portfolio optimisation.",
  "Imodeli lekhoma iyacaciswa e-Sigaba 4.2.",
];

const PANELS = [
  { id: "A", startMs: 0,     durMs: 10000 },
  { id: "B", startMs: 10000, durMs: 8000  },
  { id: "C", startMs: 18000, durMs: 9000  },
  { id: "D", startMs: 27000, durMs: 8000  },
];

export default function AltaraUnderstandV2Demo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [tMs, setTMs] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => setTMs(Date.now() - start), 80);
    return () => clearInterval(id);
  }, []);
  const active = PANELS.find(p => tMs >= p.startMs && tMs < p.startMs + p.durMs)?.id || "D";

  const scene = (
    <div className="relative w-full h-full overflow-hidden" data-testid="understand-v4" style={{ background: NAVY }}>
      <AnimatePresence mode="wait">
        {active === "A" && <PanelA key="A" />}
        {active === "B" && <PanelB key="B" />}
        {active === "C" && <PanelC key="C" />}
        {active === "D" && <PanelD key="D" />}
      </AnimatePresence>
      <div className="absolute bottom-4 left-0 right-0 text-center pointer-events-none">
        <div className="text-[10px] tracking-[0.5em]" style={{ color: GOLD }}>ALTARA · UNDERSTAND · NAVI</div>
      </div>
    </div>
  );
  if (embed) return <div className="w-screen h-screen" style={{ background: NAVY }}>{scene}</div>;
  return <div className="w-full" style={{ background: NAVY, minHeight: "100vh" }}>
    <div className="mx-auto" style={{ maxWidth: 1600, aspectRatio: "16/9" }}>{scene}</div>
  </div>;
}

/* Typewriter — reveals `text` character-by-character over `duration` ms */
function useTypewriter(text, durationMs, startMs = 0) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const start = Date.now() + startMs;
    const id = setInterval(() => {
      const t = Date.now() - start;
      if (t < 0) return;
      setN(Math.min(text.length, Math.floor(text.length * t / durationMs)));
    }, 40);
    return () => clearInterval(id);
  }, [text, durationMs, startMs]);
  return text.slice(0, n);
}

/* Chat bubble shared component */
function NaviBubble({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="flex items-start gap-3 max-w-3xl"
    >
      <div className="flex items-center justify-center rounded-full flex-shrink-0"
           style={{ width: 40, height: 40, background: GOLD }}>
        <Sparkles size={22} color={NAVY} strokeWidth={2.4} />
      </div>
      <div className="rounded-2xl rounded-tl-sm px-5 py-4"
           style={{ background: "rgba(255,255,255,0.08)", border: `1px solid ${GOLD}44`, color: CREAM, fontSize: 20, lineHeight: 1.4 }}>
        {children}
      </div>
    </motion.div>
  );
}

/* Panel A · Nomsa types a question in English */
function PanelA() {
  const typed = useTypewriter(QUESTION_EN, 4500);
  const showAnswer = typed.length >= QUESTION_EN.length - 3;
  return (
    <motion.div className="absolute inset-0 flex flex-col justify-center px-16"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}>
      <div className="uppercase tracking-widest text-xs mb-2" style={{ color: GOLD }}>ASK NAVI · YOUR PROPOSAL</div>
      <div className="text-white text-3xl font-bold mb-8">Nomsa · <span style={{ color: GOLD, fontWeight: 400 }}>typing…</span></div>
      <div className="flex flex-col gap-6 max-w-4xl">
        {/* User bubble (typing) */}
        <div className="flex justify-end">
          <div className="rounded-2xl rounded-tr-sm px-5 py-4"
               style={{ background: GOLD, color: NAVY, fontSize: 22, lineHeight: 1.4, maxWidth: 900, fontWeight: 500 }}>
            {typed}<span className="inline-block ml-1" style={{ animation: "blink 0.9s steps(2) infinite" }}>▎</span>
          </div>
        </div>
        {showAnswer && (
          <NaviBubble delay={0.15}>
            {ANSWER_EN_LINES.map((l, i) => (
              <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 + i * 0.4 }}>
                {l}
              </motion.div>
            ))}
            <motion.div className="mt-3 flex items-center gap-2 text-sm" style={{ color: GOLD }}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0 }}>
              <FileText size={14} />
              <span>Source: ABC Wealth · Disclosure v1.0 · §4.2</span>
            </motion.div>
          </NaviBubble>
        )}
      </div>
      <style>{`@keyframes blink { 50% { opacity: 0; } }`}</style>
    </motion.div>
  );
}

/* Panel B · Language toggle · English → siSwati */
function PanelB() {
  const [lang, setLang] = useState("EN");
  useEffect(() => {
    const t = setTimeout(() => setLang("SS"), 2200);
    return () => clearTimeout(t);
  }, []);
  const question = lang === "EN" ? QUESTION_EN : QUESTION_SS;
  const answerLines = lang === "EN" ? ANSWER_EN_LINES : ANSWER_SS_LINES;
  return (
    <motion.div className="absolute inset-0 flex flex-col justify-center px-16"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="uppercase tracking-widest text-xs mb-1" style={{ color: GOLD }}>ASK NAVI · IN HER LANGUAGE</div>
          <div className="text-white text-3xl font-bold">Same question. Her tongue.</div>
        </div>
        {/* Language toggle */}
        <div className="flex items-center gap-4">
          <Globe size={22} color={GOLD} />
          <div className="flex rounded-full p-1" style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${GOLD}55` }}>
            {["EN", "SS"].map(l => (
              <motion.button key={l} className="px-6 py-2 rounded-full text-sm font-semibold"
                animate={{
                  backgroundColor: lang === l ? GOLD : "transparent",
                  color: lang === l ? NAVY : CREAM,
                }}
                transition={{ duration: 0.4 }}>
                {l === "EN" ? "English" : "siSwati"}
              </motion.button>
            ))}
          </div>
        </div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={lang} className="flex flex-col gap-6 max-w-4xl"
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.45 }}>
          <div className="flex justify-end">
            <div className="rounded-2xl rounded-tr-sm px-5 py-4"
                 style={{ background: GOLD, color: NAVY, fontSize: 22, lineHeight: 1.4, maxWidth: 900, fontWeight: 500 }}>
              {question}
            </div>
          </div>
          <NaviBubble>
            {answerLines.map((l, i) => <div key={i}>{l}</div>)}
            <div className="mt-3 flex items-center gap-2 text-sm" style={{ color: GOLD }}>
              <FileText size={14} />
              <span>Source: ABC Wealth · Disclosure v1.0 · §4.2</span>
            </div>
          </NaviBubble>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}

/* Panel C · Altara features */
function PanelC() {
  const items = [
    { Icon: FileText,    title: "Grounded in her proposal",  sub: "NAVI reads the actual document. Not a generic FAQ.", delay: 0.3 },
    { Icon: ShieldCheck, title: "Every answer is cited",     sub: "Section and page number, every time. Auditable.",    delay: 0.7 },
    { Icon: Lightbulb,   title: "Signals build a learning path", sub: "Her questions become a lesson, prompt, or private support signal.", delay: 1.1 },
  ];
  return (
    <motion.div className="absolute inset-0 flex flex-col items-center justify-center px-16"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}>
      <div className="uppercase tracking-widest text-xs mb-2" style={{ color: GOLD }}>THIS IS ALTARA · UNDERSTAND</div>
      <div className="text-white text-4xl font-extrabold mb-10">Every conversation is a learning signal.</div>
      <div className="grid grid-cols-3 gap-8 w-full" style={{ maxWidth: 1400 }}>
        {items.map(({ Icon, title, sub, delay }) => (
          <motion.div key={title}
            className="rounded-2xl p-8"
            style={{ background: "rgba(255,255,255,0.05)", border: `1.5px solid ${GOLD}` }}
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
            <div className="flex items-center justify-center rounded-xl mb-5"
                 style={{ width: 60, height: 60, background: GOLD }}>
              <Icon size={30} strokeWidth={2.2} color={NAVY} />
            </div>
            <div className="text-white text-xl font-bold mb-2">{title}</div>
            <div className="text-base" style={{ color: ACCENT, opacity: 0.88, lineHeight: 1.5 }}>{sub}</div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

/* Panel D · Education becomes contextual */
function PanelD() {
  return (
    <motion.div className="absolute inset-0 flex flex-col items-center justify-center"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}>
      <motion.div className="uppercase tracking-[0.5em] text-sm mb-6" style={{ color: GOLD }}
        initial={{ opacity: 0, letterSpacing: "0.2em" }} animate={{ opacity: 1, letterSpacing: "0.5em" }}
        transition={{ duration: 1.0 }}>
        The outcome
      </motion.div>
      <motion.div className="text-white text-6xl font-extrabold text-center leading-tight"
        style={{ textShadow: "0 4px 24px rgba(0,0,0,0.6)" }}
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.8 }}>
        Education becomes contextual.
      </motion.div>
      <motion.div className="mt-4" style={{ height: 3, width: 120, background: GOLD, transformOrigin: "left" }}
        initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
        transition={{ delay: 1.1, duration: 0.7 }}
      />
    </motion.div>
  );
}
